import React, {
  KeyboardEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  faClose,
  faRefresh,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  showTranslated,
  translate,
  TranslatorKeys,
} from "../../../../config/translator";
import { ClavaContext } from "../../../../config/contexts";
import {
  Ad,
  Blog,
  ExternalVideo,
  LeagueListElement,
  Location,
  MatchListElement,
  TeamListElement,
  Translation,
  User,
} from "../../../../client/api";
import { IDType } from "../../../../config/types";
import { getMainLeague } from "../../../../config/utils";
import ClavaTextInput from "../../../components/ClavaTextInput";

type Searchable =
  | TeamListElement
  | Ad
  | LeagueListElement
  | Location
  | Blog
  | ExternalVideo
  | User
  | MatchListElement
  | { id: IDType; name: Translation };

type SearchInputProps<T extends Searchable> = {
  value: string;
  searching: boolean;
  disabled?: boolean;
  onChange: (text: string) => void;
  label?: string;
  selectedItem?: T | undefined;
  name: string;
  items: T[];
  isFocused?: boolean;
  index?: number;
  className?: string;
  onSelect: (item: T | undefined) => void;
};

function SearchInput<T extends Searchable>({
  label,
  searching,
  items,
  isFocused,
  onSelect,
  value,
  className,
  index,
  selectedItem,
  name,
  disabled,
  onChange,
}: SearchInputProps<T>) {
  const { l } = useContext(ClavaContext);
  const inputElem = useRef<HTMLInputElement>(null);
  const preventSearch = useRef(false);
  useEffect(() => {
    if (isFocused && inputElem.current) inputElem.current.focus();
  }, [isFocused]);
  const onValueChange = useCallback(
    (e: string) => {
      if (preventSearch.current) preventSearch.current = false;
      else onChange(e);
    },
    [onChange]
  );
  const onClickItem = useCallback(
    (id: IDType) => {
      onSelect(items.find((i) => i.id === id));
    },
    [items, onSelect]
  );
  const onReset = useCallback(() => {
    if (!disabled) {
      onChange("");
      onSelect(undefined);
    }
  }, [disabled, onChange, onSelect]);
  const onKeyDownHandler = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      if (e.key === "Backspace" && selectedItem) {
        preventSearch.current = true;
        onSelect(undefined);
        onChange(value.slice(0, -1));
      }
    },
    [onChange, onSelect, selectedItem, value]
  );
  return (
    <div>
      <div className="relative">
        <ClavaTextInput
          label={label}
          autoCorrect="off"
          autoComplete="off"
          disabled={disabled || !!selectedItem}
          innerRef={inputElem}
          title={
            selectedItem &&
            "leagues" in selectedItem &&
            selectedItem.leagues &&
            getMainLeague(selectedItem.leagues)
              ? selectedItem.leagues
                  .map((leag) => `[${leag.id}] ${showTranslated(leag.name, l)}`)
                  .join(", ")
              : ""
          }
          value={
            selectedItem
              ? `[${selectedItem.id}] ${
                  "username" in selectedItem
                    ? selectedItem.username
                    : "title" in selectedItem
                    ? showTranslated(selectedItem.title, l)
                    : "imageMobile" in selectedItem
                    ? `${selectedItem.name} ${translate(
                        selectedItem.position as unknown as TranslatorKeys,
                        l
                      )}`
                    : "team1" in selectedItem
                    ? `${showTranslated(
                        selectedItem.team1.name,
                        l
                      )} - ${showTranslated(selectedItem.team2.name, l)}`
                    : showTranslated(selectedItem.name, l)
                }`
              : value
          }
          onChange={onValueChange}
          onKeyDown={onKeyDownHandler}
          iconL={faSearch}
        />

        {searching && (
          <FontAwesomeIcon
            icon={faRefresh}
            className="w-4 h-4 bottom-3.5 right-3 absolute"
          />
        )}
        {!!selectedItem && !disabled && (
          <button
            type="button"
            onClick={onReset}
            className="bottom-1 right-1 absolute p-1"
          >
            <FontAwesomeIcon icon={faClose} className="w-4 h-4" />
          </button>
        )}
      </div>
      {!!items.length && !selectedItem && (
        <div className="flex flex-col items-stretch justify-start">
          {items.map((i) => (
            <button
              key={`search-result${name}-${i.id}`}
              type="button"
              onClick={() => {
                onClickItem(i.id);
              }}
              title={
                "leagues" in i && i.leagues && getMainLeague(i.leagues)
                  ? i.leagues
                      .map(
                        (leag) => `[${leag.id}] ${showTranslated(leag.name, l)}`
                      )
                      .join(", ")
                  : ""
              }
              className="border-b border-b-light-gray dark:border-b-light-gray-dark p-2 hover:bg-input-bg dark:hover:bg-input-bg-dark flex flex-row items-center justify-start"
            >
              <span className="id">[{i.id}]</span>
              <span className="font-semibold">
                {"username" in i
                  ? i.username
                  : "title" in i
                  ? showTranslated(i.title, l)
                  : "imageMobile" in i
                  ? i.name
                  : "team1" in i
                  ? `${showTranslated(i.team1.name, l)} - ${showTranslated(
                      i.team2.name,
                      l
                    )}`
                  : showTranslated(i.name, l)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

SearchInput.defaultProps = {
  disabled: false,
  isFocused: undefined,
  index: undefined,
  className: undefined,
  selectedItem: undefined,
  label: undefined,
};

export default SearchInput;
// reloa d
